import { computed, ref, shallowRef } from 'vue'
import { IFireOutbreakRepository } from '../../../../@core/fire-risk-dashboard/domain/repositories/IFireOutbreak.repository'
import { useMessages } from '../../../../composables/useMessages'
import { FireOutbreakSchema } from '../../../../@core/fire-risk-dashboard/infra/fire-outbreak-repository-adapter/schemas/FireOutbreak.schema'
import { FireOutbreak } from '../../../../@core/fire-risk-dashboard/domain/entities/FireOutbreak'
import { orderBy, isEqual } from 'lodash'
import { FireOutbreakReactionEnum } from '../../../../@core/fire-risk-dashboard/domain/enum/FireOutbreakReaction.enum'
import { ShareFireOutbreakAlertEnum } from '../enums/ShareFireOutbreakAlert.enum'
import { ShareContentProvider } from '../../@shared/providers/ShareContent.provider'
import { FireOutbreakAlertWhatsappMessageFactory } from '../factories/FireOutbreakAlertWhatsappMessage.factory'
import { FireAlertsFilterDto } from '../dtos/FireAlertsFilter.dto'
import { IRecentFireRiskRepository } from '../../../../@core/fire-risk-dashboard/domain/repositories/IRecentFireRisk.repository'
import { RecentFireRisk } from '../../../../@core/fire-risk-dashboard/domain/entities/RecentFireRisk'
import { useDashboardFireRisk } from '../stores/DashboardFireRisk.store'
import { IMonitoredAreaRepository } from '../../../../@core/monitored-area/domain/repositories/IMonitoredArea.repository'
import { MonitoredArea } from '../../../../@core/monitored-area/domain/entities/MonitoredArea'
import { feature, featureCollection, centroid } from '@turf/turf'
import { Feature, FeatureCollection, LineString } from 'geojson'
import { FireRiskColorFactory } from '../../../../@core/fire-risk-dashboard/domain/factories/FireRiskColor.factory'
import { isAfter, subHours, subDays, format } from 'date-fns'
import { ExportDataProvider } from '../../@shared/providers/ExportData.provider'
import { MapProvider } from '../../../ui/map/providers/Map.provider'
import { MapBoxDrawEnum } from '../../../ui/map/enums/MapBoxDrawMode.enum'
import { Map } from 'mapbox-gl'
import { length } from '@turf/turf'

export class FireRiskDashboardViewModel {
	constructor(
		private _monitoredAreaRepository: IMonitoredAreaRepository,
		private _fireOutbreakRepository: IFireOutbreakRepository,
		private _recentFireRiskRepository: IRecentFireRiskRepository,
	) {}

	private _addMessage = useMessages().addMessage

	public recentFireRisks = ref<RecentFireRisk[]>([])
	public monitoredAreas = ref<MonitoredArea[]>([])
	public fireOutbreakAlerts = ref<FireOutbreak[]>([])

	drawnLineString = ref<Feature>()

	drawLineEvent(draw?: MapboxDraw) {
		if (!draw) {
			console.warn('Draw was not emitted from MapBoxMap component')
			return
		}

		const lineString = draw
			.getAll()
			.features.find((feature) => feature.geometry.type === 'LineString') as Feature<LineString>

		MapProvider.clearLineString(draw)
		this.drawnLineString.value = undefined

		if (
			draw.getMode() === MapBoxDrawEnum.DRAW_LINE_STRING &&
			lineString &&
			lineString.geometry.coordinates.length === 3
		) {
			draw.changeMode(MapBoxDrawEnum.SIMPLE_SELECT)
		}
	}

	getCreatedDraw(featureCollection: Feature) {
		this.drawnLineString.value = { ...featureCollection, properties: { cor: '#F82027', borda: 5 } }
	}

	mapInstance = shallowRef<Map | undefined>(undefined)

	setMapListeners({ map, draw }: { map: Map; draw?: MapboxDraw }) {
		map.on('click', () => this.drawLineEvent(draw))
		map.on('draw.create', (draw: any) => this.getCreatedDraw(draw.features.at(0)))
		this.mapInstance.value = map
	}

	rulerDistanceLabel = computed(() => {
		if (!this.drawnLineString.value) return undefined

		const distance = length(this.drawnLineString.value)

		return `Distância medida: ${distance.toFixed(2)}km`
	})

	public filteredFireOutbreakAlerts = computed(() => {
		return this.orderedFireOutbreakAlerts.value.filter((alert) => this._filterFireAlert(alert))
	})

	public last24HFireOutbreaks = computed(() => {
		const now = new Date(new Date().setHours(23, 59, 0))
		const twentyFourHoursAgo = subHours(now, 24)
		return this.orderedFireOutbreakAlerts.value.filter((fireRisk) =>
			isAfter(new Date(fireRisk.statedAt), twentyFourHoursAgo),
		)
	})

	public mapFireOutbreaks = computed(() => {
		if (!isEqual(this.initFireAlertsFilter, this.fireAlertsFilter.value))
			return this.filteredFireOutbreakAlerts.value

		return this.last24HFireOutbreaks.value
	})

	public selectedFarmsIds = computed({
		get() {
			return useDashboardFireRisk().selectedMonitoredAreasIds
		},
		set(value: string[]) {
			useDashboardFireRisk().selectedMonitoredAreasIds = value
		},
	})

	// Deve ser chamado no setup da página, antes de qualquer busca: o store é
	// persistido em localStorage e a instância sobrevive ao logout dentro da SPA.
	discardStateFromOtherCustomer() {
		useDashboardFireRisk().discardStateFromOtherCustomer(localStorage.getItem('customerId'))
	}

	public initFireAlertsFilter = {
		farms: [],
		startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
		endData: format(new Date(), 'yyyy-MM-dd'),
		risks: [],
		occurrences: [],
		reactions: [],
		detectionConfidence: [0, 100],
	}

	public fireAlertsFilter = ref<FireAlertsFilterDto>({ ...this.initFireAlertsFilter })

	public showFarmInfo = ref(false)

	public selectedMonitoredFarms = computed(() =>
		this.monitoredAreas.value.filter((farm) => this.selectedFarmsIds.value?.includes(farm.id)),
	)

	public selectedFarmCentroids = computed(() => {
		return this.selectedMonitoredFarms.value.map((farm) => {
			const center = centroid(farm.geojson as any)
			return {
				id: farm.id,
				name: farm.name,
				longitude: center.geometry.coordinates[0],
				latitude: center.geometry.coordinates[1],
			}
		})
	})

	public farms = computed(() =>
		this.monitoredAreas.value!.map((farm) => {
			return { label: farm.name, value: farm.id }
		}),
	)

	orderedFireOutbreakAlerts = computed(() =>
		orderBy(
			this.fireOutbreakAlerts.value,
			(alert) => [new Date(alert.statedAt).getTime(), alert.id],
			'desc',
		),
	)

	geojson = computed(() => {
		const farmsGeoJson = this.selectedMonitoredFarms.value.map((farm) =>
			feature(farm.geojson, { cor: this._findGeoJSONColor(farm.id), preenchimento: 0.5 }),
		)

		const buffersGeoJson = this.selectedMonitoredFarms.value
			.filter((farm) => farm.buffer)
			.map((farm) => feature(farm.buffer!))

		if (this.drawnLineString.value) {
			return featureCollection([
				...buffersGeoJson,
				...farmsGeoJson,
				this.drawnLineString.value,
			]) as FeatureCollection
		}

		return featureCollection([...buffersGeoJson, ...farmsGeoJson]) as FeatureCollection
	})

	// A seleção de fazendas vem do localStorage e pode estar desatualizada (fazendas
	// removidas ou, se o descarte por cliente falhar, de outro cliente). Só sobrevive
	// o que pertence ao cliente atual; se nada sobrar, volta a selecionar todas.
	async loadMonitoredFarms() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._monitoredAreaRepository.getSetted(customerId!)
			this.monitoredAreas.value = response

			const monitoredAreasIds = response.map((farm) => farm.id)

			const validSelectedFarmsIds =
				this.selectedFarmsIds.value?.filter((id) => monitoredAreasIds.includes(id)) ?? []

			this.selectedFarmsIds.value = validSelectedFarmsIds.length
				? validSelectedFarmsIds
				: monitoredAreasIds
		} catch (error) {
			this._addMessage({
				description: (error as Error).message || 'Não foi possível recuperar fazendas monitoradas',
				type: 'error',
			})
		}
	}

	recieveFireOutbreakAlert(alert: string) {
		try {
			const fireOutbreakAlert = FireOutbreakSchema.parse(JSON.parse(alert))
			if (!this.selectedFarmsIds.value?.includes(fireOutbreakAlert.farmId)) return
			fireOutbreakAlert.seen = false
			this.fireOutbreakAlerts.value.push(fireOutbreakAlert)
		} catch {
			this._addMessage({ description: 'Erro ao receber alerta de foco de incêndio', type: 'error' })
		}
	}

	async rateFireOutbreak(reaction: FireOutbreakReactionEnum, fireOutbreakHash: string) {
		try {
			await this._fireOutbreakRepository.rate({ hash: fireOutbreakHash, rate: reaction })
			this._addMessage({ description: 'Foco de incêndio avaliado com sucesso', type: 'info' })
			this.getRecentFireOutbreaks()
		} catch {
			this._addMessage({ description: 'Erro ao avaliar foco de incêndio', type: 'error' })
		}
	}

	private _filterFireAlert(fireAlert: FireOutbreak) {
		let isFromFarm = true
		let isRiskType = true
		let isOccurrence = true
		let isReaction = true
		let isBetweenConfidenceRange = true

		if (this.fireAlertsFilter.value.farms?.length)
			isFromFarm = this.fireAlertsFilter.value.farms.includes(fireAlert.farmId)

		if (this.fireAlertsFilter.value.risks?.length)
			isRiskType = this.fireAlertsFilter.value.risks.includes(fireAlert.dangerLevel)

		if (this.fireAlertsFilter.value.occurrences?.length)
			isOccurrence = this.fireAlertsFilter.value.occurrences.includes(fireAlert.occurrence)

		if (this.fireAlertsFilter.value.reactions?.length)
			isReaction = this.fireAlertsFilter.value.reactions.includes(fireAlert.reaction!)

		const [minDetectionConfidence, maxDetectionConfidence] =
			this.fireAlertsFilter.value.detectionConfidence

		isBetweenConfidenceRange =
			fireAlert.detectionConfidence >= minDetectionConfidence &&
			fireAlert.detectionConfidence <= maxDetectionConfidence
		return isFromFarm && isRiskType && isOccurrence && isReaction && isBetweenConfidenceRange
	}

	handleShareFireOutbreakAlert(shareTo: ShareFireOutbreakAlertEnum, fireOutbreak: FireOutbreak) {
		switch (shareTo) {
			case ShareFireOutbreakAlertEnum.WHATSAPP:
				this.shareFireOutbreakAlertToWhatsapp(fireOutbreak)
				break
		}
	}

	shareFireOutbreakAlertToWhatsapp(fireOutbreak: FireOutbreak): void {
		const message = FireOutbreakAlertWhatsappMessageFactory.make(fireOutbreak)
		ShareContentProvider.shareToWhatsapp(message)
	}

	clearFilter() {
		this.fireAlertsFilter.value = { ...this.initFireAlertsFilter }
	}

	async getRecentFireRisks() {
		try {
			const response = await this._recentFireRiskRepository.getByMonitoredAreas(
				this.selectedFarmsIds?.value,
			)
			this.recentFireRisks.value = response.sort((a, b) => (a.fmaPlus < b.fmaPlus ? 1 : -1))
		} catch {
			this._addMessage({ description: 'Erro ao buscar riscos recentes de incêncio', type: 'error' })
		}
	}

	async getRecentFireOutbreaks() {
		try {
			const response = await this._fireOutbreakRepository.getMostRecentFireOutbreaks({
				monitoredAreasIds: this.selectedFarmsIds?.value,
				startDate: this.fireAlertsFilter.value.startDate!,
				endDate: this.fireAlertsFilter.value.endData!,
			})
			this.fireOutbreakAlerts.value = response
		} catch (error) {
			console.log(error)
			this._addMessage({ description: 'Erro ao buscar focos recentes de incêncio', type: 'error' })
		}
	}

	showChartSection = computed({
		get() {
			return useDashboardFireRisk().showChartSection
		},
		set(value: boolean) {
			useDashboardFireRisk().showChartSection = value
		},
	})

	showMapSection = computed({
		get() {
			return useDashboardFireRisk().showMapSection
		},
		set(value: boolean) {
			useDashboardFireRisk().showMapSection = value
		},
	})

	showTableSection = computed({
		get() {
			return useDashboardFireRisk().showTableSection
		},
		set(value: boolean) {
			useDashboardFireRisk().showTableSection = value
		},
	})

	daysBefore = computed({
		get() {
			return useDashboardFireRisk().daysBefore
		},
		set(value: number) {
			if (value < 1) return
			useDashboardFireRisk().daysBefore = value
		},
	})

	private _findGeoJSONColor(monitoredAreaId: string) {
		const recentFireRisk = this.recentFireRisks.value.find(
			(fireRisk) => fireRisk.id === monitoredAreaId,
		)

		if (!recentFireRisk) return

		return FireRiskColorFactory.make(recentFireRisk!.risk)
	}

	exportDataTable() {
		const columns = [
			{
				title: 'Detecção',
				key: 'detectedAtLabel',
			},
			{
				title: 'Comunicação',
				key: 'statedAtLabel',
			},
			{
				title: 'Fazenda',
				key: 'farm',
			},
			{
				title: 'Confiança',
				key: 'detectionConfidenceLabel',
			},
			{
				title: 'Satélite',
				key: 'satellite',
			},
			{
				title: 'Radiância',
				key: 'radianceLabel',
			},
			{
				title: 'Risco Climático',
				key: 'riskLabel',
			},
			{
				title: 'Local',
				key: 'occurrenceLabel',
			},
			{
				title: 'Avaliação',
				key: 'reaction',
			},
		]

		ExportDataProvider.downloadCSV(
			this.filteredFireOutbreakAlerts.value,
			columns,
			`Alertas de incêndio ${new Date().toISOString()}`,
		)
	}

	paintDrawnLine(drawnExclusionZones: FeatureCollection) {
		drawnExclusionZones.features.forEach(
			(feature) =>
				(feature.properties = {
					...feature.properties,
					cor: '#F82027',
					preenchimento: 0.1,
					borda: 5,
				}),
		)

		return drawnExclusionZones
	}

	hasActiveFilter = computed(() => {
		return !isEqual(this.initFireAlertsFilter, this.fireAlertsFilter.value)
	})
}
